// Helpers for the event form's text editors, which keep titles and descriptions as HTML.

// The font sizes offered, in px, as in Word. Any size from MIN to MAX can be typed in.
export const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72];
export const MIN_FONT_SIZE = 1;
export const MAX_FONT_SIZE = 200;

export const clampFontSize = (size) => Math.min(Math.max(Math.round(size), MIN_FONT_SIZE), MAX_FONT_SIZE);

// The next size up or down, as Word's grow and shrink buttons step: through the list, then by 10 above it
// and by 1 below it.
export const largerFontSize = (size) => {
    const next = FONT_SIZES.find((s) => s > size);
    return clampFontSize(next ?? Math.floor(size / 10) * 10 + 10);
};

export const smallerFontSize = (size) => {
    const largest = FONT_SIZES[FONT_SIZES.length - 1];
    if (size > largest) return Math.max(Math.ceil(size / 10) * 10 - 10, largest);
    const previous = [...FONT_SIZES].reverse().find((s) => s < size);
    return previous ?? clampFontSize(size - 1);
};

// The px size of each <font size>, which the editor used to write and execCommand('fontSize') still does.
const LEGACY_FONT_SIZES = { 1: 10, 2: 13, 3: 16, 4: 18, 5: 24, 6: 32, 7: 48 };

// The <font size> execCommand writes while a size is being applied; normalizeFontSizes gives it the real size.
export const FONT_SIZE_MARKER = '7';

// Turns each <font size> under root into an inline px size. Elements with the marker size get markerSize,
// and the sizes inside them are cleared so the new one shows throughout.
export const normalizeFontSizes = (root, markerSize = null) => {
    root.querySelectorAll('font[size]').forEach((el) => {
        const size = el.getAttribute('size');
        el.removeAttribute('size');
        if (size === FONT_SIZE_MARKER && markerSize != null) {
            el.style.fontSize = `${markerSize}px`;
            el.querySelectorAll('[style]').forEach((inner) => { inner.style.fontSize = ''; });
        } else if (LEGACY_FONT_SIZES[size]) {
            el.style.fontSize = `${LEGACY_FONT_SIZES[size]}px`;
        }
    });
};

// The px size of a CSS font size such as '24px', or '11pt' as Word and Google Docs write it, or null.
export const pxFontSize = (value) => {
    const match = /^([\d.]+)(px|pt)$/.exec(value?.trim() ?? '');
    if (!match) return null;
    return clampFontSize(parseFloat(match[1]) * (match[2] === 'pt' ? 4 / 3 : 1));
};

// The colour text is typed in until another is picked, first in the text colour palette.
export const DEFAULT_TEXT_COLOR = '#000000';

// Pasted HTML without black text colour, which Word and Google Docs write on all text as their default, so that
// pasted text takes the colour of the text around it. Other colours stay.
export const withoutDefaultColor = (html) => html
    .replace(/(^|[;"'\s])color\s*:\s*(#000000|#000|black|rgb\(\s*0,\s*0,\s*0\s*\))\s*(;|(?=["']))/gi, '$1')
    .replace(/\scolor=(["'])(#000000|#000|black)\1/gi, '');

export const legacyFontSize = (size) => LEGACY_FONT_SIZES[size] ?? null;

export const isWordChar = (ch) => ch != null && /[\p{L}\p{N}_]/u.test(ch);

// A colour as HTML may write it: '#9fa3a3' or, as the description's editor writes styles, 'rgb(159, 163, 163)'.
const colorPattern = (hex) => {
    const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
    return new RegExp(`${hex}|rgb\\(\\s*${r},\\s*${g},\\s*${b}\\s*\\)`, 'gi');
};

// Colours the editor shows in place of others that would be invisible on its light background: white text
// is shown grey there. colors are the store's ({ color, placeholder }).
const swapColors = (html, colors, from, to) => colors
    .filter((c) => c.placeholder)
    .reduce((result, c) => result.replace(colorPattern(c[from]), c[to].toLowerCase()), html ?? '');

export const toEditorColors = (html, colors) => swapColors(html, colors, 'color', 'placeholder');
export const fromEditorColors = (html, colors) => swapColors(html, colors, 'placeholder', 'color');

// The colour to write in the editor for a picked colour, and the picked colour a written one stands for.
const swapColor = (hex, colors, from, to) => colors.find(c => c.placeholder && c[from].toUpperCase() === hex?.toUpperCase())?.[to] ?? hex;
export const editorColor = (hex, colors) => swapColor(hex, colors, 'color', 'placeholder');
export const realColor = (hex, colors) => swapColor(hex, colors, 'placeholder', 'color');

// '#RRGGBB' for a CSS colour as queryCommandValue('foreColor') gives it ('rgb(1, 2, 3)'), or null, also for a
// transparent one such as the background of text without a highlight ('rgba(0, 0, 0, 0)').
export const toHexColor = (value) => {
    if (/^#[0-9a-f]{6}$/i.test(value ?? '')) return value.toUpperCase();
    const rgb = /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?/.exec(value ?? '');
    if (!rgb || Number(rgb[4] ?? 1) === 0) return null;
    return '#' + rgb.slice(1, 4).map((n) => Number(n).toString(16).padStart(2, '0')).join('').toUpperCase();
};
